import { LoadingSpinner } from '../LoadingSpinner';
import type { ServiceBusDeadLetterMessageDetail } from '../../services/api';
import { formatDateTime } from '../../utils/dateFormat';

interface DeadLetterMessageDetailModalProps {
  isOpen: boolean;
  queueName?: string;
  sequenceNumber?: number;
  detail?: ServiceBusDeadLetterMessageDetail;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  isActionPending: boolean;
  onClose: () => void;
  onResubmit: () => void;
  onDelete: () => void;
  onEditAndResubmit: (body?: string | null) => void;
}

export function DeadLetterMessageDetailModal({
  isOpen,
  queueName,
  sequenceNumber,
  detail,
  isLoading,
  isError,
  errorMessage,
  isActionPending,
  onClose,
  onResubmit,
  onDelete,
  onEditAndResubmit,
}: DeadLetterMessageDetailModalProps) {
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="service-bus-message-detail-title" className="max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <h2 id="service-bus-message-detail-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Dead Letter Message Detail
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Queue <span className="font-medium text-gray-700 dark:text-gray-200">{queueName ?? '—'}</span> · Sequence #{sequenceNumber ?? '—'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>

        <div className="max-h-[calc(92vh-72px)] space-y-5 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex min-h-[220px] items-center justify-center">
              <LoadingSpinner showLabel label="Loading message detail..." />
            </div>
          ) : null}

          {!isLoading && isError ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300" role="alert">
              Failed to load message detail. {errorMessage}
            </div>
          ) : null}

          {!isLoading && !isError && detail ? (
            <>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={onResubmit}
                  disabled={isActionPending}
                  className="inline-flex items-center gap-2 rounded-md bg-primary-700 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RepeatIcon className="h-4 w-4" />
                  Resubmit
                </button>
                <button
                  type="button"
                  onClick={() => onEditAndResubmit(detail.body)}
                  disabled={isActionPending}
                  className="inline-flex items-center gap-2 rounded-md border border-primary-700 px-3 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60 dark:text-primary-300 dark:hover:bg-primary-900/20"
                >
                  <EditIcon className="h-4 w-4" />
                  Edit &amp; Resubmit
                </button>
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={isActionPending}
                  className="inline-flex items-center gap-2 rounded-md border border-red-600 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:text-red-300 dark:hover:bg-red-900/20"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete
                </button>
              </div>

              <section className="grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/30 md:grid-cols-3">
                <SummaryChip label="Message ID" value={detail.messageId} mono />
                <SummaryChip label="Dead Letter Reason" value={detail.deadLetterReason} />
                <SummaryChip label="Delivery Count" value={detail.deliveryCount} />
              </section>

              <section className="rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/20">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">Headers</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <MetadataItem label="Correlation ID" value={detail.correlationId} />
                  <MetadataItem label="Subject" value={detail.subject} />
                  <MetadataItem label="Content Type" value={detail.contentType} />
                  <MetadataItem label="Dead Letter Error" value={detail.deadLetterErrorDescription} />
                </div>
              </section>

              <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <div>
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">Body</h3>
                  <pre className="max-h-[320px] overflow-auto rounded-md border border-gray-200 bg-gray-50 p-4 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                    {formatJsonBody(detail.body)}
                  </pre>
                </div>
                <div>
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">Application Properties</h3>
                  <pre className="max-h-[320px] overflow-auto rounded-md border border-gray-200 bg-gray-50 p-4 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                    {formatJsonBody(JSON.stringify(detail.applicationProperties ?? {}, null, 2))}
                  </pre>
                </div>
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">System Properties</h3>
                <div className="rounded-md border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900/30">
                  <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    <MetadataItem label="Enqueued At" value={formatOptionalDate(detail.enqueuedTimeUtc)} />
                    <MetadataItem label="Dead-Lettered At" value={formatOptionalDate(detail.deadLetterTimeUtc)} />
                    <MetadataItem label="Expires At" value={formatOptionalDate(detail.expiresAt)} />
                    <MetadataItem label="Session ID" value={detail.sessionId} />
                    <MetadataItem label="Size (bytes)" value={detail.size} />
                    <MetadataItem label="Dead Letter Source" value={detail.deadLetterSource} />
                  </dl>
                </div>
              </section>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SummaryChip({ label, value, mono }: { label: string; value?: string | number | null; mono?: boolean }) {
  return (
    <div className="rounded-md bg-white px-3 py-2 dark:bg-gray-800">
      <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className={`mt-1 break-all text-sm text-gray-900 dark:text-gray-100 ${mono ? 'font-mono text-xs' : ''}`}>
        {value === undefined || value === null || value === '' ? '—' : String(value)}
      </dd>
    </div>
  );
}

function MetadataItem({ label, value, mono }: { label: string; value?: string | number | null; mono?: boolean }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
      <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className={`mt-1 break-words text-sm text-gray-900 dark:text-gray-100 ${mono ? 'font-mono text-xs' : ''}`}>
        {value === undefined || value === null || value === '' ? '—' : String(value)}
      </dd>
    </div>
  );
}

function formatJsonBody(value?: string | null): string {
  if (!value) {
    return 'No message body available.';
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

function formatOptionalDate(value?: string | null): string {
  return value ? formatDateTime(value) : '—';
}

function RepeatIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 3h5v5" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 21H3v-5" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8a8 8 0 0 0-13-3l-2 2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16a8 8 0 0 0 13 3l2-2" />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 20h4l10-10-4-4L4 16v4z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l4 4" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 7l1 12h10l1-12M9 7V5h6v2" />
    </svg>
  );
}
